import React from "react";
import { Badge } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";

/**
 * PKT-0441 F2 · the bell badge could not fail.
 *
 * `unreadCount = notificationsData?.unread_count ?? 0` plus MUI's default
 * `Badge` behaviour (hidden at 0) meant a FAILED notification read rendered
 * pixel-identical to a healthy "nothing to report": a bare, calm bell. The menu
 * was already honest — but the operator only learns of the failure by OPENING
 * it. The at-a-glance surface, the one actually read, was structurally
 * incapable of distinguishing zero from did-not-answer.
 *
 * The error badge is distinguished by TEXT ("!" where a healthy empty renders
 * nothing at all) and by SHAPE (a 2px dashed outline where no other state has a
 * border) — per the packet's RP-2 standard, never by colour alone. It is not a
 * fabricated count: an error is not a number.
 */
export default function NotificationBellBadge({
  unreadCount,
  loading,
  error,
}: {
  unreadCount: number;
  loading: boolean;
  error: boolean;
}) {
  if (error) {
    return (
      <Badge
        badgeContent="!"
        color="warning"
        slotProps={{
          badge: {
            "aria-label": "Couldn't read notifications",
            style: {
              borderStyle: "dashed",
              borderWidth: "2px",
              borderColor: "#ed6c02",
            },
          },
        }}
      >
        <NotificationsIcon />
      </Badge>
    );
  }

  if (loading) {
    return (
      <Badge
        badgeContent="…"
        color="default"
        slotProps={{
          badge: {
            "aria-label": "Checking notifications",
            style: { borderStyle: "solid", borderWidth: "1px", borderColor: "#bdbdbd" },
          },
        }}
      >
        <NotificationsIcon />
      </Badge>
    );
  }

  // Healthy read. A real 0 hides the badge (nothing to report); a real count
  // shows the digit. Neither carries a border, so neither can be mistaken for
  // the error state in a screenshot or in greyscale.
  return (
    <Badge
      badgeContent={unreadCount}
      color="error"
      slotProps={{
        badge: {
          "aria-label":
            unreadCount > 0
              ? `${unreadCount} unread notifications`
              : "No notifications",
          style: { borderStyle: "none", borderWidth: "0px" },
        },
      }}
    >
      <NotificationsIcon />
    </Badge>
  );
}
